/**
 * Per-WebView shipper identity.
 *
 * Every mini-app is served from the same origin, so cookies, localStorage and
 * Cache Storage are shared between WebViews of different shippers. Only
 * sessionStorage is scoped to a single WebView, which makes it the one place
 * that reliably remembers which shipper this WebView was opened for.
 */
const TAB_SHIPPER_KEY = 'tab_shipper_id'

export function getTabShipperId(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return sessionStorage.getItem(TAB_SHIPPER_KEY)
  } catch {
    return null
  }
}

export function setTabShipperId(shipperId: string) {
  if (typeof window === 'undefined' || !shipperId) return
  try {
    sessionStorage.setItem(TAB_SHIPPER_KEY, shipperId)
  } catch {
    /* storage unavailable: fall back to cookie/SSR shipper */
  }
}

/**
 * Resolve this WebView's shipper: URL entry param wins (a new mini-app entry),
 * then what this WebView already remembered, then the SSR bootstrap value.
 */
export function resolveTabShipperId(
  fallbackShipperId?: string | null
): string | null {
  if (typeof window === 'undefined') return fallbackShipperId || null

  const fromUrl = new URLSearchParams(window.location.search).get('shipper_id')
  const resolved = fromUrl || getTabShipperId() || fallbackShipperId || null

  if (resolved) setTabShipperId(resolved)
  return resolved
}

/** Re-assert this WebView's shipper in the shared (httpOnly) cookies. */
export async function syncShipperCookie(shipperId: string): Promise<boolean> {
  try {
    const res = await fetch('/api/shipper', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ shipper_id: shipperId }),
      credentials: 'same-origin',
      cache: 'no-store',
      keepalive: true,
    })
    if (!res.ok) return false
    const data = await res.json().catch(() => null)
    return Boolean(data?.changed)
  } catch {
    return false
  }
}
